import { Reducer } from "redux";
import SystemStatusAction, {
    IAddPrioritySystemStatusAction,
    IFetchPrioritySystemStatusesFulfilledAction,
    IFetchSystemStatusSettingsFulfilledAction,
    IFetchSystemStatusSettingsPendingAction,
    IFetchSystemStatusSettingsRejectedAction,
    IFetchSystemStatusUnreadCountFulfilledAction,
    IFetchSystemStatusOngoingCountFulfilledAction,
    IPostSystemStatusViewedPendingAction,
    ISetShowOutOfSeasonAction,
    ISetCurrentResourceAction,
    ISetSystemStatusUnreadCountAction
} from "./actions";
import SystemStatusActionTypes from "./action-types";

export interface ISystemStatusState {
    currentResource?: Spintr.ISystemStatusResource;
    prioritySystemStatuses: Spintr.ISocialPostBase[];
    viewedPrioritySystemStatuses: number[];
    unreadCount: number;
    ongoingCount: number;
    settings?: Spintr.ISystemStatusSettings;
    settingsLoading: boolean;
    showOutOfSeason: boolean;
}

type Handler<TType> = (state: ISystemStatusState, action: TType) => ISystemStatusState;

const initialState: ISystemStatusState = {
    prioritySystemStatuses: [],
    viewedPrioritySystemStatuses: [],
    unreadCount: 0,
    ongoingCount: 0,
    settingsLoading: false,
    showOutOfSeason: false,
};

const handleSetCurrentResource: Handler<ISetCurrentResourceAction> = (state, action) => ({
    ...state,
    currentResource: action.currentResource,
});

const handleSetUnreadCount: Handler<ISetSystemStatusUnreadCountAction> = (state, action) => ({
    ...state,
    unreadCount: action.unreadCount,
});

const handleFetchOngoingCountFulfilled: Handler<IFetchSystemStatusOngoingCountFulfilledAction> = (state, action) => ({
    ...state,
    ongoingCount: action.payload.data,
});

const handleAddPrioritySystemStatus: Handler<IAddPrioritySystemStatusAction> = (state, action) => {
    // Ignore if status already exists (fetched before realtime message was received) or we have already removed it
    if (state.prioritySystemStatuses.some((s) => s.Id === action.systemStatus.Id) ||
        state.viewedPrioritySystemStatuses.includes(action.systemStatus.Id)) {
        return state;
    }

    return {
        ...state,
        prioritySystemStatuses: [...state.prioritySystemStatuses, action.systemStatus],
    };
};

const handleFetchPrioritySystemStatusesFulfilled: Handler<IFetchPrioritySystemStatusesFulfilledAction> = (state, action) => ({
    ...state,
    prioritySystemStatuses: action.payload.data,
});

const handleFetchUnreadCountFulfilled: Handler<IFetchSystemStatusUnreadCountFulfilledAction> = (state, action) => ({
    ...state,
    unreadCount: action.payload.data,
});

const handleFetchSettingsPending: Handler<IFetchSystemStatusSettingsPendingAction> = (state) => ({
    ...state,
    settingsLoading: true,
});

const handleFetchSettingsRejected: Handler<IFetchSystemStatusSettingsRejectedAction> = (state) => ({
    ...state,
    settingsLoading: false,
    settings: {
        useSeasons: false,
        seasons: []
    },
});

const handleFetchSettingsFulfilled: Handler<IFetchSystemStatusSettingsFulfilledAction> = (state, action) => ({
    ...state,
    settingsLoading: false,
    settings: action.payload.data,
});

const handleSetShowOutOfSeason: Handler<ISetShowOutOfSeasonAction> = (state, action) => ({
    ...state,
    showOutOfSeason: action.showOutOfSeason,
});

const handlePostSystemStatusViewedPending: Handler<IPostSystemStatusViewedPendingAction> = (state, action) => {
    const postId = state.prioritySystemStatuses.find((s) => s.content.some((c) => c.Id === action.meta))?.Id;

    return {
        ...state,
        prioritySystemStatuses: state.prioritySystemStatuses.filter((s) => !s.content.some(c => c.Id === action.meta)),
        viewedPrioritySystemStatuses: [...state.viewedPrioritySystemStatuses, postId],
    }
};

const handlerMapping = {
    [SystemStatusActionTypes.SetCurrentResource]: handleSetCurrentResource,
    [SystemStatusActionTypes.SetUnreadCount]: handleSetUnreadCount,
    [SystemStatusActionTypes.FetchOngoingCountFulfilled]: handleFetchOngoingCountFulfilled,
    [SystemStatusActionTypes.AddPrioritySystemStatus]: handleAddPrioritySystemStatus,
    [SystemStatusActionTypes.FetchPrioritySystemStatusesFulfilled]: handleFetchPrioritySystemStatusesFulfilled,
    [SystemStatusActionTypes.FetchUnreadCountFulfilled]: handleFetchUnreadCountFulfilled,
    [SystemStatusActionTypes.PostSystemStatusViewedPending]: handlePostSystemStatusViewedPending,
    [SystemStatusActionTypes.FetchSettingsPending]: handleFetchSettingsPending,
    [SystemStatusActionTypes.FetchSettingsRejected]: handleFetchSettingsRejected,
    [SystemStatusActionTypes.FetchSettingsFulfilled]: handleFetchSettingsFulfilled,
    [SystemStatusActionTypes.SetShowOutOfSeason]: handleSetShowOutOfSeason,
};


export const reducer: Reducer<ISystemStatusState, SystemStatusAction> =
    (state = initialState, action) => handlerMapping[action.type]
        ? handlerMapping[action.type].call(null, state, action)
        : state;

export default reducer;

